import { HttpClient, HttpErrorResponse, HttpEvent, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, throwError } from 'rxjs';
import { FormUtil } from '../utils/form.util';
import { MatSnackBar } from '@angular/material/snack-bar';

enum HttpMethods {
  get = 'GET',
  post = 'POST',
  put = 'PUT',
  delete = 'DELETE',
  options = 'OPTIONS',
}

export interface ApiGateway {
  request(url: string, method: HttpMethods, body: any);
}

@Injectable({
  providedIn: 'root'
})
export class ApiGatewayService implements ApiGateway {

  constructor(
    private _http: HttpClient,
    private _snackbar: MatSnackBar
  ) { }

  /**
    * Sends a generic HTTP request.
    * @param url The URL to send the request to.
    * @param method The HTTP method (GET, POST, PUT, DELETE, etc.).
    * @param body Optional body to send with the request.
    * @returns An Observable containing the response data.
    */
  request(url: string, method: string, body?: any): Observable<any> {
    const req = new HttpRequest(method, url, body); // Create a new HttpRequest
    return this._http.request(req).pipe(
      map((resp: HttpEvent<unknown>) => resp), // Map the response to return it directly
      catchError((error) => this.handleError(error.status, error.code)) // Handle errors
    );
  }

  /**
   * Sends a GET request to the specified URL.
   * @param url The URL to send the request to.
   * @param config Optional configuration for the request.
   * @param silenceError Optional flag to suppress error logging.
   * @returns An Observable containing the response data or null in case of an error.
   */
  get(url: string, config?: any, silenceError: boolean = false): Observable<any> {
    const reqObservable = this._http.get(url, config);
    return this.processResponse(reqObservable, silenceError);
  }

  /**
   * Sends a POST request to the specified URL with the provided payload.
   * @param url The URL to send the request to.
   * @param payload The data to send in the request body.
   * @param processPayload Optional flag to process the payload.
   * @param silenceError Optional flag to suppress error logging.
   * @returns An Observable containing the response data or null in case of an error.
   */
  post(url: string, payload: any, processPayload: boolean = true, silenceError: boolean = false): Observable<any> {
    if (processPayload) {
      payload = FormUtil.processForm(payload); // Process the payload if required
    }

    const reqObservable = this._http.post(url, payload);
    return this.processResponse(reqObservable, silenceError);
  }

  /**
   * Sends a PUT request to the specified URL with the provided payload.
   * @param url The URL to send the request to.
   * @param payload The data to send in the request body.
   * @param processPayload Optional flag to process the payload.
   * @param silenceError Optional flag to suppress error logging.
   * @returns An Observable containing the response data or null in case of an error.
   */
  put(url: string, payload: any, processPayload: boolean = true, silenceError: boolean = false): Observable<any> {
    if (processPayload) {
      payload = FormUtil.processForm(payload); // Process the payload if required
    }

    const reqObservable = this._http.put(url, payload);
    return this.processResponse(reqObservable, silenceError);
  }

  /**
   * Sends a DELETE request to the specified URL.
   * @param url The URL to send the request to.
   * @param silenceError Optional flag to suppress error logging.
   * @returns An Observable containing the response data or null in case of an error.
   */
  delete(url: string, silenceError: boolean = false): Observable<any> {
    const reqObservable = this._http.delete(url);
    return this.processResponse(reqObservable, silenceError);
  }

  /**
   * Processes the response from an Observable and handles any errors that occur.
   * @param obs The Observable to process.
   * @param silenceErrors Optional flag to suppress error notifications.
   * @returns An Observable that emits the response or an error.
   */
  private processResponse(obs: Observable<any>, silenceErrors: boolean = false): Observable<any> {
    return obs.pipe(
      catchError((error: HttpErrorResponse) => {
        return this.handleError(error, silenceErrors); // Handle errors using handleError method
      })
    );
  }

  /**
   * Handles HTTP errors by extracting relevant information and optionally displaying a snackbar notification.
   * @param error The error response from the HTTP request.
   * @param silenceError Flag to suppress error notifications.
   * @returns An Observable that emits an error with the error code and status.
   */
  private handleError(error: HttpErrorResponse, silenceError: boolean): Observable<Error> {
    let errorCode: string;
    let errorStatus: string;

    // Extracting error details from the response
    const errorItem = error.error && Array.isArray(error.error) && error.error.length ? error.error[0] : null;

    if (errorItem) {
      errorCode = errorItem.code;
      errorStatus = errorItem.desc;
    }
    if (error) {
      errorCode = error.status.toString();
      errorStatus = error.statusText;
    } else {
      // Fallback for generic error messages
      errorCode = 'UNKNOWN_ERROR';
      errorStatus = 'An unknown error occurred.';
    }

    if (!silenceError) {
      this.displaySnackbar(errorCode, errorStatus);
    }

    // Return an observable that throws the error with code and status
    return throwError(() => new Error(`${errorCode}: ${errorStatus}`));
  }

  /**
   * Displays a snackbar notification with the error code and status.
   * @param errorCode The error code to display.
   * @param errorStatus The error status message to display.
   */
  private displaySnackbar(errorCode: string, errorStatus: string) {
    this._snackbar.open(errorCode, errorStatus, {
      panelClass: 'error', // CSS class for styling the snackbar
      duration: 5000, // Duration for which the snackbar will be visible
    });
  }
}